import React, { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { AxiosRequestConfig } from 'axios';
import DOMPurify from 'dompurify';

import {
  SelectInput,
  BaseProps,
  NO_RESULTS,
} from 'Shared/components/FormInput/Variants/Select';
import { FormInputErrorMessage } from 'Shared/components';
import { LazytApiExecuteFuncType } from 'Shared/services/Api/makeHooks';
import { isPresent } from 'Shared/utils';
import { TypeaheadResult } from '../TypeaheadResult';
import styles from './styles.module.css';

type TypeaheadResponse = Array<{
  id: string;
}>;

type TypeaheadProps<T> = Omit<BaseProps, 'options'> & {
  className?: string;
  name: string;
  setValue: (value: string) => void;
  onSelect: (selectedId: string, options: T) => void;
  onBlur?: (e: React.FocusEventHandler<HTMLInputElement>) => void;
  lazyRequest: LazytApiExecuteFuncType<T>;
  getLazyRequestConfig?: (value: string) => AxiosRequestConfig;
  transformOptions: (options: T) => BaseProps['options'];
  debounce?: number;
  floating?: boolean;
};

const noResultsFound: BaseProps['options'] = {
  [NO_RESULTS]: <TypeaheadResult primaryResult="No Results Found" />,
};

export const Typeahead = <T extends TypeaheadResponse>({
  className,
  name,
  label,
  value,
  setValue,
  onSelect,
  onBlur,
  loading,
  lazyRequest,
  getLazyRequestConfig,
  transformOptions,
  disabled,
  debounce,
  isClearable,
  floating,
  onClear,
  ...props
}: TypeaheadProps<T>) => {
  const [options, setOptions] = useState<TypeaheadResponse>([]);
  const [noResults, setNoResults] = useState<boolean>(false);
  const debounced = useDebouncedCallback(async v => {
    if (v.length > 0) {
      // clear options before request
      setOptions([]);
      setNoResults(false);

      // if creating a config sanitize the input value
      try {
        const response = await lazyRequest(
          getLazyRequestConfig?.(DOMPurify.sanitize(v))
        );

        if (response.data.length > 0) {
          setOptions(response.data);
        } else {
          setNoResults(true);
        }
      } catch (err) {
        console.error(err);

        // clear options and show no results found if we have an error
        setOptions([]);
        setNoResults(true);
      }
    }
  }, debounce);

  // do not show options when request is loading
  const transformedOptions =
    !loading && value
      ? noResults
        ? noResultsFound
        : transformOptions(options as T)
      : {};

  return (
    <div className={styles.wrapper}>
      <SelectInput
        {...props}
        className={className}
        variant={floating ? 'floatingTypeahead' : 'typeahead'}
        name={name}
        loading={loading}
        options={transformedOptions}
        onSelect={(selectedId: string) => {
          onSelect(selectedId, options as T);
        }}
        onChange={(v: string) => {
          // downshift work arounds to avoid setting the same value or briefly setting the key on selection
          if (v !== value && !Object.keys(transformedOptions).includes(v)) {
            setValue(v);
            debounced(v);
          }
        }}
        onBlur={onBlur}
        value={value}
        label={label}
        disabled={disabled}
        isClearable={isClearable}
        onClear={onClear}
      />
      {isPresent(props.errorMessage) && (
        <FormInputErrorMessage {...props} message={props.errorMessage} />
      )}
    </div>
  );
};
